import React from "react";
import { completeTelegramLogin, loadTelegramProfile, setActiveUserCsrfToken, submitUserCommand, userRequest } from "./userApi";
import { configureCommandSession } from "../commands/commandTransport";
import { clearPendingReferral, pendingReferralCode } from "../referrals/referralLink";

const UserSessionContext = React.createContext(null);

export function UserSessionProvider({ children }) {
  const [status, setStatus] = React.useState("loading");
  const [session, setSession] = React.useState(null);
  const [snapshot, setSnapshot] = React.useState(null);
  const [transientProfile, setTransientProfile] = React.useState(null);
  const [error, setError] = React.useState(null);
  const channel = React.useRef(null);
  const authGeneration = React.useRef(0);
  const identity = React.useRef(null);
  const referralAttempts = React.useRef(new Set());

  const clearProtectedState = React.useCallback(() => {
    authGeneration.current += 1; identity.current = null;
    setActiveUserCsrfToken(null); configureCommandSession(null);
    setSession(null); setSnapshot(null); setTransientProfile(null); setStatus("logged-out");
  }, []);

  const refresh = React.useCallback(async () => {
    const requestedGeneration = ++authGeneration.current;
    try {
      const nextSession = await userRequest("/insights/account/session");
      if (requestedGeneration !== authGeneration.current) return null;
      if (!nextSession.authenticated) { clearProtectedState(); return null; }
      if (identity.current !== nextSession.sessionId) { setSnapshot(null); setTransientProfile(null); identity.current = nextSession.sessionId; }
      setActiveUserCsrfToken(nextSession.csrfToken); configureCommandSession(nextSession);
      setSession(nextSession); setStatus("authenticated"); setError(null);
      try { const nextSnapshot = await userRequest("/insights/account/snapshot"); if (requestedGeneration === authGeneration.current) setSnapshot(nextSnapshot); }
      catch (snapshotError) { if (requestedGeneration !== authGeneration.current) return null; if (snapshotError.status === 401) clearProtectedState(); else if (snapshotError.status !== 404) setError(snapshotError); }
      return nextSession;
    } catch (requestError) {
      if (requestedGeneration !== authGeneration.current) return null;
      if (requestError.status === 401) clearProtectedState(); else { setStatus("error"); setError(requestError); }
      return null;
    }
  }, [clearProtectedState]);

  React.useEffect(() => {
    setActiveUserCsrfToken(session?.csrfToken);
    configureCommandSession(session);
  }, [session]);

  React.useEffect(() => {
    localStorage.removeItem("chatId");
    localStorage.removeItem("chartEvaluationSession");
    sessionStorage.removeItem("chartEvaluationSession");
    channel.current = typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel("cryptember-user-session");
    window.addEventListener("cryptember-auth-expired", clearProtectedState);
    if (channel.current) channel.current.onmessage = (event) => { if (event.data?.type === "LOGOUT") clearProtectedState(); else refresh(); };
    refresh();
    return () => { channel.current?.close(); window.removeEventListener("cryptember-auth-expired", clearProtectedState); };
  }, [clearProtectedState, refresh]);

  const submitCommand = React.useCallback(async (input) => {
    if (!session?.csrfToken) throw new Error("Authentication required.");
    const result = await submitUserCommand(input);
    if (result.acknowledgement?.accepted) await refresh();
    return result;
  }, [session, refresh]);

  const submitReferral = React.useCallback(async () => {
    const referralCode = pendingReferralCode(); if (!referralCode) return;
    const attemptKey = `${session?.sessionId || "current"}:${referralCode}`;
    if (referralAttempts.current.has(attemptKey)) return;
    referralAttempts.current.add(attemptKey);
    try {
      const result = await submitCommand({ type: "REFERRAL_ASSIGN", payload: { referralCode } });
      const code = result.acknowledgement?.code;
      if (["ASSIGNED", "ALREADY_ASSIGNED", "INVALID_CODE", "SELF_REFERRAL"].includes(code)) clearPendingReferral();
    } catch (_) { }
  }, [submitCommand, session?.sessionId]);

  React.useEffect(() => {
    if (status === "authenticated" && session?.csrfToken && pendingReferralCode()) submitReferral();
  }, [status, session, submitReferral]);

  const login = React.useCallback(async (telegramPayload, mode = "WIDGET") => {
    const loginGeneration = ++authGeneration.current;
    setStatus("authenticating"); setError(null);
    try {
      const result = await completeTelegramLogin(mode, telegramPayload);
      if (loginGeneration !== authGeneration.current) return null;
      setSession(result); setTransientProfile(result.transientProfile || null); setStatus("authenticated");
      await refresh(); channel.current?.postMessage({ type: "SESSION_CHANGED" }); return result;
    } catch (loginError) { if (loginGeneration === authGeneration.current) { clearProtectedState(); setError(loginError); } throw loginError; }
  }, [clearProtectedState, refresh]);

  const logout = React.useCallback(async () => {
    const logoutCsrfToken = session?.csrfToken;
    clearProtectedState(); channel.current?.postMessage({ type: "LOGOUT", generation: Date.now() });
    if (logoutCsrfToken) { try { await userRequest("/insights/account/logout", { method: "POST", body: {}, csrfToken: logoutCsrfToken }); } catch (_) { } }
  }, [session, clearProtectedState]);

  const sessionCsrfToken = session?.csrfToken;
  const requestTransientProfile = React.useCallback(async () => {
    if (!sessionCsrfToken) return null;
    const requestedGeneration = authGeneration.current;
    try { const profile = await loadTelegramProfile(sessionCsrfToken); if (requestedGeneration !== authGeneration.current) return null; setTransientProfile(profile); return profile; }
    catch (_) { return null; }
  }, [sessionCsrfToken]);

  const clearTransientProfile = React.useCallback(() => setTransientProfile(null), []);

  const value = React.useMemo(() => ({ status, session, snapshot, transientProfile, error, login, logout, refresh,
    submitCommand, requestTransientProfile, clearTransientProfile }),
  [status, session, snapshot, transientProfile, error, login, logout, refresh, submitCommand, requestTransientProfile, clearTransientProfile]);
  return <UserSessionContext.Provider value={value}>{children}</UserSessionContext.Provider>;
}

export function useUserSession() {
  const value = React.useContext(UserSessionContext);
  if (!value) throw new Error("useUserSession must be used within UserSessionProvider.");
  return value;
}
